import moment from "moment"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { getIds } from "../../core/utils/getIds"
import { useScope } from "../SelectProjectCompanyContainer"
import Send from "../../core/communication"
import ActivityHistoryScreen from "../../screen/ActivityHistoryScreen"
import { useSelector } from "react-redux"

const periodOptions = [
  {
    value: "all",
    label: "Todo o período",
  },
  {
    value: "today",
    label: "Hoje",
  },
  {
    value: "lastWeek",
    label: "Últimos 7 dias",
  },
  {
    value: "lastMonth",
    label: "Últimos 30 dias",
  },
]

const loadHistoryActivity = async (optionsToQuery, filterDate) => {
  const { projectsByScope, projectId, userId, scopeType } = optionsToQuery || {};
  const { startDate, endDate } = filterDate || {}

  const projectsQuery = projectsByScope
    ? getIds(projectsByScope.map(({ id }) => id))
    : `'${projectId}'`;

  const userQuery = userId ? `producer=${userId}&` : "";
  const dateQuery = startDate !== "" && endDate !== "" ? `startDate=${moment(startDate, 'DD/MM/YYYY').format("YYYY-MM-DD")}&endDate=${moment(endDate, 'DD/MM/YYYY').format("YYYY-MM-DD")}&` : ""

  const url = `/events-sector/activity/${projectId}`;
  const queryString = `${userQuery}${dateQuery}projects=${projectsQuery}&scopeType=${scopeType}`;

  
  try {
    const response = await Send.get(url, queryString);
    return response;
  } catch (error) {
    console.error('Error loading history activity:', error);
    throw error;
  }
};

const loadActivityDistribution = async (optionsToQuery, technicalList, technicianSelect, isUserAdmin, user) => {
  const { projectsByScope, projectId, userId, scopeType } = optionsToQuery || {};
  const isTechnicianSelected = technicianSelect?.value !== '1';
  const technicianIds = isTechnicianSelected ? `'${technicianSelect.value}'` : '';

  const projectsQuery = projectsByScope
    ? getIds(projectsByScope.map(({ id }) => id))
    : `'${projectId}'`;

  const url = `/events-sector/activity/distribution`;
  const userQuery = userId ? `producer=${userId}&` : "";
  const queryString = `${userQuery}tecId=${technicianIds}&projects=${projectsQuery}&scopeType=${scopeType}`;

  try {
    const response = await Send.get(url, queryString);
    return response;
  } catch (error) {
    console.error('Error loading activity distribution:', error);
    throw error;
  }
};

const loadAllTec = async (optionsToQuery) => {
  const { projectsByScope, projectId, scopeType } = optionsToQuery || {};

  const projectsQuery = projectsByScope
  ? getIds(projectsByScope.map(({ id }) => id))
  : `'${projectId}'`;
  
  try {
    const response = await Send.get('/users/tec-or-manager', `projects=${projectsQuery}&scopeType=${scopeType}&role=technician`);
    return response.map(row => ({ value: row?.id, label: row?.name }))
  } catch(error) {
    console.log("Error => ", error);
    customToast("Erro ao carregar técnicos", 'error');
  }
}

const ActivityHistoryContainer = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { userId } = useParams();
  const { projectsByScope, scopeType, scope } = useScope();
  const { producer, projectId, creationUserDate } = state || {};
  const isUserAdmin = useSelector((state) => state?.app?.isUserAdmin)
  const { user } = useSelector((state) => state?.app?.user)

  const [selectTechnician, setSelectTechnician] = useState({
    value: "1",
    label: "Todos"
  });

  const [filterDate, setFilterDate] = useState({
    startDate:  moment().startOf('year').format("DD/MM/YYYY"), // moment(creationUserDate).format("DD/MM/YYYY") || 
    endDate: moment().format("DD/MM/YYYY"),
  });
  const [selectPeriod, setSelectPeriod] = useState({
    value: "all",
    label: "Todo o período"
  });
  const [selectedActivity, setSelectedActivity] = useState(null)

  const optionsToQuery = createOptionsToQuery(userId, projectId, scopeType, projectsByScope, scope);

  const queryConfig = {
    enabled: !!optionsToQuery.projectId,
    staleTime: Infinity,
    refetchInterval: Infinity,
    cacheTime: 0,
  };

  const { data, isLoading } = useQuery(
    ["activity-history", optionsToQuery?.projectId, selectPeriod.value, filterDate?.startDate, filterDate?.endDate],
    () => loadHistoryActivity(optionsToQuery, filterDate),
    queryConfig
  );

  const { data: technicalList, isFetched: technicalListFetched } = useQuery(
    ["tec-distribution", optionsToQuery.projectId],
    () => loadAllTec(optionsToQuery),
    queryConfig
  );

  const { data: distributionActivityData, isLoading: isDistributionActivityLoading } = useQuery(
    ["activity-distribution", technicalListFetched, selectTechnician?.value],
    () => loadActivityDistribution(optionsToQuery, technicalList, selectTechnician, isUserAdmin, user),
    queryConfig
  );

  const handlePeriodSelect = (period) => {
    const { value } = period || {};
    setSelectPeriod(period)
    const today = moment().format("DD/MM/YYYY");
    
    let startDate;
  
    switch (value) {
      case "all":
        startDate = moment().format("DD/MM/YYYY") || moment().startOf('year').format("DD/MM/YYYY");
        break;
      case "lastWeek":
        startDate = moment().subtract(6, 'days').format("DD/MM/YYYY");
        break;
      case "today":
        startDate = today;
        break;
      default:
        startDate = moment().subtract(29, 'days').format("DD/MM/YYYY");
        break;
    }
  
    setFilterDate({ startDate, endDate: today });
  };

  const handleTechnicianSelect = (tec) => {
    setSelectTechnician(tec)
    setSelectedActivity(null)
  };

  const handleChangeDate = (field, value) => {
    setFilterDate((prevDates) => ({
      ...prevDates,
      [field]: value,
    }));

    setSelectPeriod({
      value: "all",
      label: "Todo o período"
    })
  };

  const handleFilterActivities = (e, i) => {
    setSelectedActivity(e.name)
  }

  const handleGoBack = () => {
    navigate(-1);
  };

  const technicianOptions = technicalList?.length > 0 
  ? [{value: '1', label: 'Todos'}, ...technicalList] 
  : [{value: '1', label: 'Todos'}]

  return (
    <ActivityHistoryScreen
      data={filterActivities(data, selectTechnician, technicalList, selectedActivity)}
      distributionActivityData={distributionActivityData}
      periodOptions={periodOptions}
      technicianOptions={technicianOptions}
      handlePeriodSelect={handlePeriodSelect}
      handleTechnicianSelect={handleTechnicianSelect}
      handleFilterActivities={handleFilterActivities}
      selectTechnician={selectTechnician}
      handleChangeDate={handleChangeDate}
      filterDate={filterDate}
      handleGoBack={handleGoBack}
      producer={producer}
      selectPeriod={selectPeriod}
      isLoading={isLoading}
      isUserAdmin={isUserAdmin}
      isDistributionActivityLoading={isDistributionActivityLoading}
    />
  );
};

const filterActivities = (data, technician, technicalList, selectedActivity) => {
  let filteredData = data || [];

  const filterByTechnician = (activities, technician, technicalList) => {
    if (!technician || !technician.value) {
      return activities;
    }

    if (technician.value === "1") {
      return activities;
    }

    return activities.filter(item =>
      !item.technician || item.technician.id === technician.value
    );
  };

  filteredData = filteredData.map(obj => {
    const activitiesWithTechnician = obj?.activities.filter(item => item.technician && item.technician.id);
    const activitiesWithoutTechnician = obj?.activities.filter(item => !item.technician || !item.technician.id);

    return {
      ...obj,
      activities: [
        ...filterByTechnician(activitiesWithTechnician, technician, technicalList),
        ...activitiesWithoutTechnician
      ]
    };
  }).filter(obj => obj.activities.length > 0);

  if (selectedActivity) {
    filteredData = filteredData.map(obj => ({
      ...obj,
      activities: obj.activities.filter(item => item.type === selectedActivity),
    })).filter(obj => obj.activities.length > 0);
  }

  return filteredData;
};

const getUserIdFromSearch = (search) => {
  const userId = search && search?.split('?farmerId=')[1]
  return userId
};

export const createOptionsToQuery = (userId, projectId, scopeType, projectsByScope, scope) => {
  return userId
    ? { projectId, scopeType: "project", userId }
    : { projectId: scope?.id, scopeType, projectsByScope };
};

export default ActivityHistoryContainer;
